/**
 * Chip — filterchip (#152, mangler i MAPPING.md §3).
 *
 * En knapp med `aria-pressed`. Valgt er fylt (`bg-solid text-on-solid`);
 * ikke valgt er omrisset glass som en sekundærknapp. 44 px høy (mål ≥ 44 px),
 * radius pill, 14 px / 700 — Oppgaver-skissens `chipOn`/`chipOff`.
 *
 * `count` gir telleboblen (CountBadge) til høyre, med mindre høyre-padding så
 * boblen ligger 6 px fra kanten slik skissen viser. Telleren er synlig tekst og
 * blir derfor en del av knappens tilgjengelige navn («Mine 2»).
 *
 * `tone="warn"` er allergichipen fra Oppsett-skissen (#159, steg 9f): valgt er
 * amber (`bg-warn-bg` med en innfelt `warn-line`-kant og ink-tekst), så en valgt
 * allergi ikke ser ut som et vanlig filter. `chipClass` gir de samme klassene
 * til en `<label>` rundt en radioknapp, der en knapp med `aria-pressed` ville
 * vært feil rolle.
 *
 * `Pill` er fortsatt etiketten som ikke kan trykkes på.
 */
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { CountBadge } from './CountBadge';

export type ChipTone = 'default' | 'warn';

const SELECTED_CLASS: Record<ChipTone, string> = {
  default: 'bg-solid text-on-solid',
  // Ink, ikke `text-warn`: warn på amber faller under 4.5:1 i gløden i mørkt
  // tema, og allergiflatene skal holde kravet overalt (contrast.test.ts).
  warn: 'bg-warn-bg text-ink ring-[1.5px] ring-inset ring-warn-line',
};

/** Form- og fargeklassene til en chip, uten padding. */
export function chipClass(selected: boolean, tone: ChipTone = 'default'): string {
  return [
    'inline-flex h-11 shrink-0 items-center gap-2.5 rounded-pill text-sm font-bold transition-colors',
    selected
      ? SELECTED_CLASS[tone]
      : 'bg-secondary text-ink border border-secondary-line hover:bg-inset',
  ].join(' ');
}

interface ChipOwnProps {
  selected?: boolean;
  tone?: ChipTone;
  count?: number;
  children: ReactNode;
}

type ChipProps = Omit<ComponentPropsWithRef<'button'>, 'aria-pressed'> & ChipOwnProps;

export function Chip({
  selected = false,
  tone = 'default',
  count,
  className,
  children,
  type = 'button',
  ...rest
}: ChipProps) {
  const hasCount = count !== undefined && count > 0;
  return (
    <button
      type={type}
      aria-pressed={selected}
      className={[
        chipClass(selected, tone),
        hasCount ? 'pl-4 pr-1.5' : 'px-3.5',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
      {hasCount ? <CountBadge count={count} size="md" data-testid="chip-count" /> : null}
    </button>
  );
}
